import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Upload, Image as ImageIcon, Trash2, Copy, Check } from 'lucide-react';

export default function MediaManager() {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [copiedUrl, setCopiedUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `public/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('episode-data')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('episode-data').getPublicUrl(filePath);

      alert(`File uploaded successfully!\n\nURL: ${publicUrl}`);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(''), 2000);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-horizon text-3xl text-yellow-400 mb-2">Media Library</h1>
        <p className="text-gray-400">Upload and manage images and media files</p>
      </div>

      <div className="bg-gradient-to-br from-purple-900/20 to-black border-2 border-dashed border-purple-600/50 rounded-xl p-12 text-center mb-6 hover:border-purple-600 transition-colors">
        <div className="mb-6">
          <div className="inline-block bg-purple-500/20 p-6 rounded-full mb-4">
            <Upload className="text-purple-400" size={48} />
          </div>
          <h3 className="text-white font-bold text-xl mb-2">Upload Media</h3>
          <p className="text-gray-400 mb-4">
            Drag and drop files here or click to browse
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleUpload}
          disabled={uploading}
          accept="image/*"
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className={`inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 transition-colors cursor-pointer ${
            uploading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <ImageIcon size={20} />
          {uploading ? 'Uploading...' : 'Choose File'}
        </label>
      </div>

      <div className="bg-gradient-to-br from-red-900/20 to-black border border-yellow-400/30 rounded-xl p-6">
        <h2 className="font-horizon text-2xl text-yellow-400 mb-4">Upload Instructions</h2>
        <div className="space-y-4 text-gray-300">
          <div>
            <h3 className="text-yellow-400 font-bold mb-2">1. Select File</h3>
            <p className="text-sm">
              Click the "Choose File" button and select an image from your computer.
            </p>
          </div>
          <div>
            <h3 className="text-yellow-400 font-bold mb-2">2. Upload</h3>
            <p className="text-sm">
              The file will be uploaded to Supabase storage and you'll receive a public URL.
            </p>
          </div>
          <div>
            <h3 className="text-yellow-400 font-bold mb-2">3. Use URL</h3>
            <p className="text-sm">
              Copy the URL and paste it into the image URL field when creating/editing content.
            </p>
          </div>
          <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-4 mt-4">
            <p className="text-yellow-400 font-bold text-sm mb-2">💡 Tip</p>
            <p className="text-gray-300 text-sm">
              Uploaded files are stored in the <code className="bg-black/50 px-2 py-1 rounded">episode-data</code> bucket
              and are publicly accessible via their URLs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
